import { Form, FormField, FormSection, FormType } from '../../../../../common/components/reactive-form-builder/interfaces/form.interface';
import { InputType, DropdownData, FilteredValue } from '../../../../../common/components/reactive-form-builder/interfaces/input.interface';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { environment } from 'src/environments/environment';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { MaquinariaModel } from 'src/app/models/trabajadores/maquinaria.model';
import { TareasBioModel } from 'src/app/models/tareas/tareas-bio.model';
import { TratamientosControlModel } from 'src/app/models/tratamientos/tratamientos-control.model';

export class TratamientosControlListsDefinition {
    public fincas: DropdownData<FincasModel> = { filtered: [], values: [], selected: null };
    public parcelas: DropdownData<ParcelasModel> = { filtered: [], values: [], selected: null };
    public maquinarias: DropdownData<MaquinariaModel> = { filtered: [], values: [], selected: null };

    public productos1: DropdownData<TareasBioModel> = { filtered: [], values: [], selected: null };
    public productos2: DropdownData<TareasBioModel> = { filtered: [], values: [], selected: null };
    public productos3: DropdownData<TareasBioModel> = { filtered: [], values: [], selected: null };

    public usos1: DropdownData<string> = { filtered: [], values: [], selected: null };
    public usos2: DropdownData<string> = { filtered: [], values: [], selected: null };
    public usos3: DropdownData<string> = { filtered: [], values: [], selected: null };

    public plagas1: DropdownData<string> = { filtered: [], values: [], selected: null };
    public plagas2: DropdownData<string> = { filtered: [], values: [], selected: null };
    public plagas3: DropdownData<string> = { filtered: [], values: [], selected: null };

    public todasLasParcelas: FilteredValue<ParcelasModel>[] = [];
}

export class TratamientosControlFormDefinition {
    public formType!: FormType;

    public appName = environment.appName;
    public fincaName = environment.features.fincaName;
    public parcelasName = environment.features.parcelasName;

    public lists = new TratamientosControlListsDefinition();

    public cliente = StorageManager.getClient();
    public usuario = StorageManager.getUser();

    public model: TratamientosControlModel = { estado: 'Planificado' };

    public formFields!: Form<TratamientosControlModel>;

    constructor(type: FormType) {
        this.formType = +type;
        this.formFields = this.buildForm();
    }

    public snapshotAllParcelas(): void {
        this.lists.todasLasParcelas = [...this.lists.parcelas.filtered] as FilteredValue<ParcelasModel>[];
    }

    public filterParcelasByFinca(idFinca: string | number | undefined): void {
        if (idFinca) {
            this.lists.parcelas.filtered = this.lists.todasLasParcelas.filter(
                (item) => item.value?.id_finca?.toString() === idFinca.toString()
            );
        } else {
            this.lists.parcelas.filtered = [...this.lists.todasLasParcelas];
        }
    }

    public refreshDosisSubFieldData(rowIndex: 0 | 1 | 2): void {
        const slot = (rowIndex + 1) as 1 | 2 | 3;

        this.formFields = {
            ...this.formFields,
            sections: this.formFields.sections.map((section) => {
                if (section.name !== 'Materias activas') { return section; }

                return {
                    ...section,
                    fields: section.fields.map((field) => {
                        if (field.name !== 'lineas_materias' || !field.input.repeatableGroupConfiguration) {
                            return field;
                        }

                        return {
                            ...field,
                            input: {
                                ...field.input,
                                repeatableGroupConfiguration: {
                                    ...field.input.repeatableGroupConfiguration,
                                    subFields: field.input.repeatableGroupConfiguration.subFields.map((sf: any) => {
                                        if (sf.key === 'uso') {
                                            return { ...sf, data: this.lists[`usos${slot}`] };
                                        }
                                        if (sf.key === 'plaga') {
                                            return { ...sf, data: this.lists[`plagas${slot}`] };
                                        }
                                        if (sf.key === 'producto') {
                                            return { ...sf, data: this.lists[`productos${slot}`] };
                                        }
                                        return sf;
                                    })
                                }
                            }
                        };
                    })
                };
            })
        };
    }

    private buildForm(): Form<TratamientosControlModel> {
        const action = this.formType === FormType.CREATION ? 'Crear' : 'Actualizar';

        return {
            name: 'tratamientos-control',
            header: `${action} Control de Tratamientos`,
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: { isEnabled: false, label: '' },
            signed: false,
        };
    }

    private getFormSections(): FormSection<any>[] {
        return [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getMainFields(),
                order: 1
            },
            {
                name: 'Materias activas',
                width: 12,
                isVisible: true,
                fields: this.getDosisFields(),
                order: 2
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 3
            }
        ];
    }

    private getMainFields(): FormField<any>[] {
        return [
            {
                name: 'id_finca',
                label: this.fincaName,
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.fincas,
                    dropdownConfiguration: { valuePrimaryKey: 'id' }
                },
                width: 3,
                conditions: { isRequired: true, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 1,
                vinculatedFields: undefined
            },
            {
                name: 'ids_parcelas',
                label: this.parcelasName,
                input: {
                    type: InputType.MULTISELECT,
                    isEditable: true,
                    data: this.lists.parcelas,
                    dropdownConfiguration: { valuePrimaryKey: 'id' }
                },
                width: 3,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 2,
                vinculatedFields: undefined
            },
            {
                name: 'fecha',
                label: 'Fecha',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: { isRequired: true, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 3,
                vinculatedFields: undefined
            },
            {
                name: 'estado',
                label: 'Estado',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: {
                        filtered: [
                            { label: 'Planificado', value: 'Planificado' },
                            { label: 'Orden ficha', value: 'Orden ficha' },
                            { label: 'Aplicado', value: 'Aplicado' }
                        ],
                        values: [],
                        selected: null
                    },
                    defaultValue: 'Planificado'
                },
                width: 3,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 4,
                vinculatedFields: undefined
            },
            {
                name: 'plagas',
                label: 'Nombre',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: { isRequired: true, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 5,
                vinculatedFields: undefined
            },
            {
                name: 'litros',
                label: 'Litros de caldo',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 6,
                vinculatedFields: undefined
            },
            {
                name: 'ph_caldo',
                label: 'pH Caldo',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: { isRequired: false, isVisible: this.appName === 'hernandorena', isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 7,
                vinculatedFields: undefined
            },
            {
                name: 'ph_agua',
                label: 'pH Agua',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: { isRequired: false, isVisible: this.appName === 'hernandorena', isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 8,
                vinculatedFields: undefined
            },
            {
                name: 'ph_final',
                label: 'pH Final',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: { isRequired: false, isVisible: this.appName === 'hernandorena', isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 9,
                vinculatedFields: undefined
            },
            {
                name: 'id_maquinaria',
                label: 'Maquinaria',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.maquinarias,
                    dropdownConfiguration: { valuePrimaryKey: 'id' }
                },
                width: 3,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 10,
                vinculatedFields: undefined
            },
            {
                name: 'observaciones',
                label: 'Observaciones',
                input: { type: InputType.TEXTAREA, isEditable: true },
                width: 6,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 11,
                vinculatedFields: undefined
            },
            {
                name: 'epi',
                label: 'EPI',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 12,
                vinculatedFields: undefined
            }
        ];
    }

    private getDosisFields(): FormField<any>[] {
        return [
            {
                name: 'lineas_materias',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.REPEATABLE_GROUP,
                    isEditable: true,
                    repeatableGroupConfiguration: {
                        rows: 3,
                        addButtonLabel: 'Añadir materia activa',
                        bindings: {
                            producto:       '_linea_producto',
                            uso:            '_linea_uso',
                            plaga:          '_linea_plaga',
                            dosisCuaderno:  '_linea_dosis_cuaderno',
                            udCuaderno:     '_linea_ud_cuaderno',
                            dosis:          '_linea_dosis',
                            udReal:         '_linea_ud_real',
                        },
                        serialization: { separator: ';' },
                        subFields: this.getDosisSubFields()
                    }
                },
                width: 12,
                conditions: {
                    isRememberedOnDuplicate: false,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            }
        ];
    }

    private getDosisSubFields(): any[] {
        return [
            {
                key: 'producto',
                label: 'Materia activa',
                getData: (ctx: any) => {
                    const slot = Math.min((ctx.rowIndex ?? 0) + 1, 3) as 1 | 2 | 3;
                    return this.lists[`productos${slot}`];
                },
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.productos1,
                    dropdownConfiguration: { valuePrimaryKey: 'id' }
                }
            },
            {
                key: 'uso',
                label: 'Uso',
                getData: (ctx: any) => {
                    const slot = Math.min((ctx.rowIndex ?? 0) + 1, 3) as 1 | 2 | 3;
                    return this.lists[`usos${slot}`];
                },
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.usos1
                }
            },
            {
                key: 'plaga',
                label: 'Plaga',
                getData: (ctx: any) => {
                    const slot = Math.min((ctx.rowIndex ?? 0) + 1, 3) as 1 | 2 | 3;
                    return this.lists[`plagas${slot}`];
                },
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.plagas1
                }
            },
            {
                key: 'dosisCuaderno',
                label: 'Dosis cuaderno',
                input: { type: InputType.NUMERIC, isEditable: true }
            },
            {
                key: 'udCuaderno',
                label: 'Ud. Medida Cuaderno',
                input: { type: InputType.TEXT, isEditable: true }
            },
            {
                key: 'dosis',
                label: 'Dosis aplicada',
                input: { type: InputType.NUMERIC, isEditable: true }
            },
            {
                key: 'udReal',
                label: 'Ud. Medida Aplicada',
                input: { type: InputType.TEXT, isEditable: true }
            }
        ];
    }

    private getHiddenFields(): FormField<any>[] {
        return [
            {
                name: 'id',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 1,
                vinculatedFields: undefined
            },
            {
                name: 'id_cliente',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false, defaultValue: this.cliente?.id ?? undefined },
                updateValue: () => this.cliente?.id ?? undefined,
                width: 2,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 2,
                vinculatedFields: undefined
            },
            {
                name: 'id_usuario',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false, defaultValue: this.usuario?.id ?? undefined },
                updateValue: () => this.usuario?.id ?? undefined,
                width: 2,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 3,
                vinculatedFields: undefined
            },
            {
                name: 'id_tratamiento',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 4,
                vinculatedFields: undefined
            },
            {
                name: 'id_tarea',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 5,
                vinculatedFields: undefined
            },
            {
                name: 'cultivo',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 6,
                vinculatedFields: undefined
            },
            {
                name: 'variedad',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 7,
                vinculatedFields: undefined
            },
            {
                name: 'sup_cultivada',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 8,
                vinculatedFields: undefined
            },
            ...([
                'id_producto1', 'id_producto2', 'id_producto3',
                'uso1', 'uso2', 'uso3',
                'plaga1', 'plaga2', 'plaga3',
                'dosis_cuaderno1', 'dosis_cuaderno2', 'dosis_cuaderno3',
                'ud_cuaderno_1', 'ud_cuaderno_2', 'ud_cuaderno_3',
                'dosis1', 'dosis2', 'dosis3',
                'ud_real_1', 'ud_real_2', 'ud_real_3',
            ] as const).map((name, idx) => ({
                name,
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, isEditable: true },
                width: 1,
                conditions: {
                    isRememberedOnDuplicate: false,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 100 + idx
            })),
            ...([
                '_linea_producto', '_linea_uso', '_linea_plaga',
                '_linea_dosis_cuaderno', '_linea_ud_cuaderno',
                '_linea_dosis', '_linea_ud_real',
                'lineas_materias'
            ] as const).map((name, idx) => ({
                name,
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 1,
                conditions: {
                    isRememberedOnDuplicate: false,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 200 + idx
            }))
        ];
    }
}